import React from 'react';
import { Box, Divider, Menu, Typography } from '@mui/material';
import { profileMenuProps, UserNameBox, ContentWrapper } from './HTProfileMenuStyled';
import { useTranslation } from 'react-i18next';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { useDispatch } from 'react-redux';
import { setModalWindow } from '@/state/hypertracker/hypertackerSlice';
import { MODAL_TYPE_HANDLER } from '@/lib/types/hypertrackerTypes';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router';
import { externalUrls, ROUTES } from '@/lib/constants';
import WalletDisplaySwitcher from '@/components/Hypertracker/WalletDisplaySwitcher';

const isMobileSafari = (): boolean => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const ua = navigator.userAgent;
    const isIosDevice = /iP(ad|hone|od)/.test(ua);
    const isIpadOs = /Macintosh/.test(ua) && /Mobile/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);

    return (isIosDevice || isIpadOs) && isSafari;
};

const openInNewTab = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
};

interface ProfileMenuInterface {
    anchorEl: null | HTMLElement;
    handleClose: () => void;
    open: boolean;
}

const HTProfileMenu: React.FC<React.PropsWithChildren<ProfileMenuInterface>> = ({
    anchorEl,
    handleClose,
    open,
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'profile_menu_drawer' });
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleOpenModalClick = (value) => {
        dispatch(
            setModalWindow({
                modalType: MODAL_TYPE_HANDLER.IS_AUTH_MODAL,
                authorizationModalType: value,
            }),
        );
        handleClose();
    };

    const onAboutClick = () => {
        handleClose();
        if (isMobileSafari()) {
            window.location.href = externalUrls.hypertrackerDashboard;
            return;
        }

        openInNewTab(externalUrls.hypertrackerDashboard);
    };

    const onApiClick = () => {
        handleClose();
        navigate(ROUTES.HYPERTRACKER_API);
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: profileMenuProps }}
        >
            <ContentWrapper>
                <UserNameBox>
                    <Typography variant="p12" color="customText.text1" component={'p'}>
                        {t('profile')}
                    </Typography>
                </UserNameBox>
                <Divider
                    orientation="horizontal"
                    sx={{ margin: '8px 0', width: '100%', height: '1px' }}
                />
                <Stack
                    sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '& a': {
                            textDecoration: 'none',
                        },
                        '&:hover a': {
                            color: (theme) => theme.palette.hyperLiquidColor,
                        },
                    }}
                    onClick={() => handleOpenModalClick(AUTH_TYPE.SIGN_IN)}
                >
                    <Box>
                        <Typography
                            component={'a'}
                            variant={'p12'}
                            color={'common.white'}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { color: (theme) => theme.palette.hyperLiquidColor },
                                textDecoration: 'none',
                            }}
                        >
                            {t('log_in')}
                        </Typography>
                        <Typography variant={'p12'} color={'common.white'} component={'a'}>
                            /
                        </Typography>
                        <Typography
                            component={'a'}
                            variant={'p12'}
                            color={'common.white'}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { color: (theme) => theme.palette.hyperLiquidColor },
                                textDecoration: 'none',
                            }}
                        >
                            {t('sign_up')}
                        </Typography>
                    </Box>
                    <Typography
                        component={'span'}
                        fontSize={'12px'}
                        color={'text.secondary'}
                        fontWeight={400}
                        lineHeight={'16px'}
                        mt={'8px'}
                    >
                        {t('auth_menu_text')}
                    </Typography>
                </Stack>
                <Typography
                    onClick={onApiClick}
                    variant={'p12'}
                    component={'p'}
                    color={'secondary.main'}
                    sx={{
                        marginTop: '16px',
                        cursor: 'pointer',
                        width: '100%',
                    }}
                >
                    {t('api')}
                </Typography>
                <Divider sx={{ margin: '8px 0' }} />
                <UserNameBox
                    onClick={onAboutClick}
                    sx={{
                        gap: '2px',
                        cursor: 'pointer',
                        '&:hover': { color: (theme) => theme.palette.hyperLiquidColor },
                        '&:hover svg': { color: (theme) => theme.palette.hyperLiquidColor },
                        '&:hover p': { color: (theme) => theme.palette.hyperLiquidColor },
                    }}
                >
                    <Typography variant="p12" color="common.white" component={'p'}>
                        {t('about')}
                    </Typography>
                </UserNameBox>

                <Divider sx={{ margin: '8px 0' }} />
                <WalletDisplaySwitcher onLoginPrompt={handleClose} />
            </ContentWrapper>
        </Menu>
    );
};

export default HTProfileMenu;
